import { useState, useEffect, useCallback } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { promotionService } from '../services'
import TableLoadingRow from '../components/shared/TableLoadingRow'
import TableEmptyState from '../components/shared/TableEmptyState'

const PromotionsManagePage = () => {
  const [promotions, setPromotions] = useState([])
  const [promotionsLoading, setPromotionsLoading] = useState(false)
  const [promotionsPage, setPromotionsPage] = useState(0)
  const [promotionsRowsPerPage, setPromotionsRowsPerPage] = useState(10)
  const [promotionsTotalCount, setPromotionsTotalCount] = useState(0)
  const [message, setMessage] = useState(null)

  const fetchPromotions = useCallback(async () => {
    try {
      setPromotionsLoading(true)
      const response = await promotionService.getPromotions({
        page: promotionsPage + 1,
        limit: promotionsRowsPerPage
      })
      
      const promotionsData = response.items || response.data || (Array.isArray(response) ? response : [])
      const totalCount = response.pagination?.total || response.totalCount || response.total || (Array.isArray(promotionsData) ? promotionsData.length : 0)
      
      setPromotions(Array.isArray(promotionsData) ? promotionsData : [])
      setPromotionsTotalCount(totalCount)
    } catch (error) {
      console.error('Error fetching promotions:', error)
      setPromotions([])
      setPromotionsTotalCount(0)
    } finally {
      setPromotionsLoading(false)
    }
  }, [promotionsPage, promotionsRowsPerPage])

  const handleDeletePromotion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) {
      return
    }

    try {
      await promotionService.deletePromotion(id)
      setMessage({ type: 'success', text: 'Promotion deleted successfully' })
      fetchPromotions()
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error deleting promotion',
      })
      setTimeout(() => setMessage(null), 5000)
    }
  }

  const handlePromotionsPageChange = (event, newPage) => {
    setPromotionsPage(newPage)
  }

  const handlePromotionsRowsPerPageChange = (event) => {
    setPromotionsRowsPerPage(parseInt(event.target.value, 10))
    setPromotionsPage(0)
  }

  useEffect(() => {
    fetchPromotions()
  }, [fetchPromotions])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#1a1a1a' }}>
        Manage Promotions
      </Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
        View and manage all product promotions
      </Typography>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }} 
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              All Promotions
            </Typography>
            <Button
              size="small"
              onClick={fetchPromotions}
              disabled={promotionsLoading}
            >
              {promotionsLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : 'Refresh'}
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Code (SKU)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Regular MRP</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Discount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Condition</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promotionsLoading ? (
                  <TableLoadingRow colSpan={10} />
                ) : promotions.length === 0 ? (
                  <TableEmptyState colSpan={10} message="No promotions found" />
                ) : (
                  promotions.map((promo) => (
                      <TableRow key={promo.id} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{promo.code}</TableCell>
                        <TableCell>{promo.description || 'N/A'}</TableCell>
                        <TableCell align="right">৳{parseFloat(promo.regular_mrp).toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {promo.discount_type === 'Amount' 
                            ? `৳${parseFloat(promo.discount_value).toFixed(2)}`
                            : `${parseFloat(promo.discount_value).toFixed(0)}%`
                          }
                        </TableCell>
                        <TableCell>{promo.condition}</TableCell>
                        <TableCell>
                          <Chip
                            label={promo.type || 'N/A'}
                            size="small"
                            sx={{
                              bgcolor: promo.type === 'Sales' ? '#e3f2fd' : (promo.type === 'Purchase' ? '#fff3e0' : '#f5f5f5'),
                              color: promo.type === 'Sales' ? '#1565c0' : (promo.type === 'Purchase' ? '#e65100' : '#757575'),
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(promo.start_date)}</TableCell>
                        <TableCell>{formatDate(promo.end_date)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={promo.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              bgcolor: promo.is_active ? '#e8f5e9' : '#ffebee',
                              color: promo.is_active ? '#1b5e20' : '#c62828',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeletePromotion(promo.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={promotionsTotalCount}
            page={promotionsPage}
            onPageChange={handlePromotionsPageChange}
            rowsPerPage={promotionsRowsPerPage}
            onRowsPerPageChange={handlePromotionsRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>
    </Container>
  )
}

export default PromotionsManagePage
